import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import shared from './GarageContent.module.scss';
import classes from './MediaGallery.module.scss';

// More than this and the grid ends in a "+N" tile that opens the viewer there.
const MAX_THUMBNAILS = 12;

/**
 * The full-screen viewer by itself, for media shown some other way than the
 * grid below (the photos in a conversation - InquiryThread). `items` are
 * { url, type: 'image' | 'video' }; it is open on items[openAt], closed when
 * openAt is null. onStep(delta) moves through them; describe(item, index)
 * labels each for screen readers. The arrow keys step, Escape closes, and
 * focus goes back to whatever opened it.
 */
export const MediaViewer = ({ items, openAt, onClose, onStep, label, describe }) => {
    const closeRef = useRef(null);
    const count = items.length;
    const current = openAt !== null && openAt !== undefined ? items[openAt] : null;

    const isOpen = !!current;
    useEffect(() => {
        if (!isOpen) return undefined;
        // The thumbnail that opened the viewer, to hand focus back to.
        const opener = document.activeElement;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight') onStep(1);
            else if (e.key === 'ArrowLeft') onStep(-1);
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        if (closeRef.current) closeRef.current.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
            if (opener && opener.focus) opener.focus();
        };
    }, [isOpen, onClose, onStep]);

    if (!current) return null;

    return createPortal(
        <div
            className={classes.viewer}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <button ref={closeRef} type="button" className={classes.close} onClick={onClose} aria-label="Close">
                <i className="fas fa-times" aria-hidden="true"></i>
            </button>
            <div className={classes.stage}>
                {current.type === 'video' ? (
                    <video key={current.url} src={current.url} controls autoPlay playsInline aria-label={describe(current, openAt)} />
                ) : (
                    <img key={current.url} src={current.url} alt={describe(current, openAt)} />
                )}
            </div>
            <div className={classes.controls}>
                {count > 1 && (
                    <button type="button" onClick={() => onStep(-1)} aria-label="Previous">
                        <i className="fas fa-chevron-left" aria-hidden="true"></i>
                    </button>
                )}
                <span>{openAt + 1} / {count}</span>
                {count > 1 && (
                    <button type="button" onClick={() => onStep(1)} aria-label="Next">
                        <i className="fas fa-chevron-right" aria-hidden="true"></i>
                    </button>
                )}
            </div>
        </div>,
        document.body
    );
};

/**
 * Photo and video grid for an inspection report (or a purchase's photos from
 * our yard - `label` names the set for screen readers), with a full-screen
 * viewer.
 *
 * DetailContent's InspectionGallery is built as a page section of its own
 * (title bar, close button that scrolls the page, 60px margins) so it does not
 * sit inside a garage card; this is the compact equivalent. The arrow keys
 * step through the media and Escape closes the viewer. showCounts={false}
 * drops the "N photos · N videos" line for a caller that already shows it.
 */
const MediaGallery = ({ images = [], videos = [], title = 'Car', label = 'inspection', showCounts = true }) => {
    const items = [
        ...images.map((url) => ({ url, type: 'image' })),
        ...videos.map((url) => ({ url, type: 'video' })),
    ];
    const [openAt, setOpenAt] = useState(null);
    const count = items.length;

    const close = useCallback(() => setOpenAt(null), []);
    const step = useCallback((delta) => {
        setOpenAt((at) => (at === null ? at : (at + delta + count) % count));
    }, [count]);

    if (count === 0) return null;

    const overflow = count > MAX_THUMBNAILS ? count - (MAX_THUMBNAILS - 1) : 0;
    const thumbnails = overflow ? items.slice(0, MAX_THUMBNAILS - 1) : items;
    const describe = (item, index) => `${title} - ${label} ${item.type === 'video' ? 'video' : 'photo'} ${index + 1} of ${count}`;

    return (
        <>
            {showCounts && (
                <p className={classes.counts}>
                    {[
                        images.length ? `${images.length} photo${images.length === 1 ? '' : 's'}` : null,
                        videos.length ? `${videos.length} video${videos.length === 1 ? '' : 's'}` : null,
                    ].filter(Boolean).join(' · ')}
                </p>
            )}
            <ul className={classes.grid}>
                {thumbnails.map((item, index) => (
                    <li key={`${index}-${item.url}`}>
                        <button type="button" className={classes.thumb} onClick={() => setOpenAt(index)}>
                            {item.type === 'video' ? (
                                <>
                                    {/* #t=0.1 makes Safari paint the first frame as a poster. */}
                                    <video src={`${item.url}#t=0.1`} preload="metadata" muted playsInline aria-hidden="true" />
                                    <span className={classes.play} aria-hidden="true"><i className="fas fa-play"></i></span>
                                    <span className={shared.srOnly}>{describe(item, index)}</span>
                                </>
                            ) : (
                                <img src={item.url} alt={describe(item, index)} loading="lazy" />
                            )}
                        </button>
                    </li>
                ))}
                {overflow > 0 && (
                    <li>
                        <button
                            type="button"
                            className={`${classes.thumb} ${classes.more}`}
                            onClick={() => setOpenAt(MAX_THUMBNAILS - 1)}
                        >
                            +{overflow}
                            <span className={shared.srOnly}> more - open the viewer</span>
                        </button>
                    </li>
                )}
            </ul>

            <MediaViewer
                items={items}
                openAt={openAt}
                onClose={close}
                onStep={step}
                label={`${title} - ${label} media`}
                describe={describe}
            />
        </>
    );
};

export default MediaGallery;
